import React, { useState, useEffect, useCallback } from "react";

import moment from "moment-timezone";

import { useUser } from "../../../util/user";
import Noti from "../../../util/noti";
import GYAxios from "../../../util/GYAxios";

import InvoiceTable from "./InvoiceTable";
import SummaryInvoice from "./SummaryInvoice";
import LoaderGraph from "../../general/LoaderGraph";
import { useUserProps } from "../../../util/userDBContext";

function InvoiceContent(props) {
	const user = useUser();
	const { loggedCountry } = useUserProps();
	const [country, setCountry] = useState(loggedCountry);

	const [approvalPermissions, setApprovalPermissions] = useState(null);
	const [invoiceSites, setInvoiceSites] = useState(null);
	const [summaryInvoiceSites, setSummaryInvoiceSites] = useState(null);
	const [isMakroUser, setIsMakroUser] = useState(false);
	const [isCFWUser, setIsCFWUser] = useState(false);
	const [isGYUser, setIsGYUser] = useState(false);
	const [isSoloSite, setSoloSite] = useState(false);
	const [userInfo, setUserInfo] = useState(null);
	const [freeSites, setFreeSites] = useState([]);
	const [selectedMonth, setSelectedMonth] = useState(
		moment().subtract(1, "month")
	);
	const [ready, setReady] = useState(false);

	/**
	 * Validate user's level of invoice approval
	 * @param {User's roles'} roles
	 */
	const validateApprovalLevel = (roles) => {
		const approvalPermissions = roles.filter((role) =>
			role.includes("administration:InvoiceTable:_approveInvoice")
		);
		const hasApprovalPermission = approvalPermissions.length > 0;

		if (hasApprovalPermission || roles.includes("_GY:IT")) {
			let dept = "";
			// O&M access to approve invoice
			if (roles.includes("_GY:O&M")) dept = "O&M";
			// Accounting access to approve invoice
			else if (roles.includes("_GY:Accounting") || roles.includes("_GY:Construction_InvoiceApproval")) dept = "ACC"; // in PH, Construction team could approve invoice like Accounting in VN
			// IT access to view and resolve rejected invoice
			else if (roles.includes("_GY:IT")) dept = "IT";

			const reqApprovalStatus = GYAxios.get(
				`/general_info/approval-status/${dept}`
			);

			reqApprovalStatus.then((res) => {
				if (res.data != null) setApprovalPermissions(res.data);
			});
		} else setApprovalPermissions(false);
	};

	/**
	 * Validate if user is GY member
	 * @param {Logged in user} user
	 */
	const validateGYUser = (user) => {
		const isGYUser = user.roles.includes("_GY:Base") ? true : false;
		setIsGYUser(isGYUser);
	};

	/**
	 * Validate if logged in user is Makro
	 * @param {Logged in user} user
	 */
	const validateMakroUser = (user) => {
		const siteIDStr =
			user.site_id != null && user.site_id != "all"
				? user.site_id.toString()
				: "";
		const multiSiteID = siteIDStr.split(",");

		// validate if it is a solo site
		if (siteIDStr != "" && multiSiteID.length == 1) setSoloSite(true);
		else setSoloSite(false);
		// Validate if it is a makro user
		if (user.client_id != null && user.client_id == 18) setIsMakroUser(true);
		else setIsMakroUser(false);
	};

	/**
	 * Validate if logged in user is CFW
	 * @param {Logged in user} user
	 */
	const validateCFWUser = (user) => {
		const siteIDStr = user.site_id != null && user.site_id != "all" ? user.site_id.toString() : "";
		const multiSiteID = siteIDStr.split(",");

		// validate if it is a solo site
		if (siteIDStr != "" && multiSiteID.length == 1) setSoloSite(true);
		else setSoloSite(false);
		// Validate if it is a CFW user
		if (user.client_id != null && user.client_id == '299' && user.roles.includes("administration:InvoiceTable:_seeSummaryInvoice-client")) setIsCFWUser(true);
		else setIsCFWUser(false);  
	};

	/**
	 * Validate permission level of invoice sites accessible
	 * by user based on roles
	 * @param {Roles of user} roles
	 */
	const validateViewLevel = (roles) => {
		let level = false;

		// O&M access to approve invoice
		if (roles.includes("_GY:O&M")) level = "O&M";
		// Accounting access to approve invoice
		else if (roles.includes("_GY:Accounting")) level = "ACC";
		// IT access to resolve invoice
		else if (roles.includes("_GY:IT")) level = "IT";
		// Clients' access to view approved invoice
		else if (roles.includes("administration:InvoiceTable:_clientViewInv"))
			level = "CLIENT";

		return level;
	};

	const viewLevel = validateViewLevel(user.roles);

	/**
	 * Validate sites to be added to Summary Invoice (Makro and CFW client)
	 * @param {Array of sites} arr
	 */
	const getSummaryInvoiceSites = (arr, freeSites) => {
		const unsortedMakroList = arr.filter((site) => site.client_id == 18  && !freeSites.includes(site.table_name));
		const unsortedCFWList = arr.filter((site) => site.client_id == 299);
		const sortedMakroList = unsortedMakroList.sort((a, b) =>
			a.site_code > b.site_code ? 1 : -1
		);const sortedCFWList = unsortedCFWList.sort((a, b) =>
			a.entity_id > b.entity_id ? 1 : -1
		);

		const sortedList = [...sortedMakroList, ...sortedCFWList];
		const formattedSites = sortedList.map((site) => ({
			...site,
			bankAccount: getBankAcc(site),
		}));
		return formattedSites;
	};

	/**
	 * Get bank account info of site
	 * @param {obj} site
	 * @returns Bank account object
	 */
	const getBankAcc = (site) => ({
		accName: site.acc_name,
		accNumber: site.acc_number,
		accNumRemark: site.acc_num_remark,
		bankName: site.bank_name,
		branch: site.branch,
		swiftCode: site.swift_code,
	});

	/**
	 * Fetch Data — uses server-side calculateInvoiceData() so the table
	 * shows exactly the same data that goes into the PDFs.
	 */
	const fetchData = async (selectedMonth, user, userInfo, country) => {
		if (user != null && userInfo != null) {
			let column = "admin";
			let id = "all";

			// Client Users
			if (user.roles.includes("administration:InvoiceTable:_clientViewInv")) {
				// Multi and single sites IDs
				if (userInfo.site_id != null && userInfo.site_id !== "all") {
					id = userInfo.site_id;
					column = "site_id";
				}
				// Client ID
				else {
					id = userInfo.client_id;
					column = "client_id";
				}
			}

			const lastMonth = selectedMonth;
			const yearMonth = lastMonth.format("YYYYMM");
			const selectedDate = lastMonth.endOf("month").format("YYYY-MM-DD");

			// Reassign columns and value for invoice API
			if (column === "admin") {
				column = "country";
				id = country;
			}

			try {
				const payload = {
					YYYYMM: yearMonth,
					level: viewLevel,
					selectedDate,
				};

				// Set filter: country or column/value
				if (column === "country") {
					payload.country = id;
				} else {
					payload.column = column;
					payload.value = id;
				}

				const response = await GYAxios.post("/invoice/table_data", payload, {
					timeout: 120000,
				});

				if (response.data && response.data.invoiceSites) {
					const sites = response.data.invoiceSites;
					const resFreeSite = response.data.freeSites || [];

					setFreeSites(resFreeSite);

					const SIsites = getSummaryInvoiceSites(sites, resFreeSite);
					setSummaryInvoiceSites(SIsites);
					setInvoiceSites(sites);
				} else {
					setSummaryInvoiceSites(null);
					setInvoiceSites(null);
				}
			} catch (error) {
				console.error("Failed to fetch invoice table data:", error);
				setSummaryInvoiceSites(null);
				setInvoiceSites(null);
			}

			setReady(true);
		}
	};

	/**
	 * Fetch country of user and set as initial country
	 */
	const initCountry = (user) => {
		const keycloakID = user.sub;
		GYAxios.get(`/users/filter?column=keycloak_id&value=${keycloakID}`).then(
			(res) => {
				const userInfo =
					res.data.error == null && res.data.length > 0 ? res.data[0] : false;

				setCountry(userInfo.country);
				setUserInfo(userInfo);
			}
		);
	};

	const makroSitesFilterByPhaseFn = useCallback(phase => site => {
		if (phase === 'PHASE_1') {
			return site.site_code.length === 5 && site.client_id == '18'
		} else if (phase === 'PHASE_2') {
			return site.site_code.length !== 5 && site.client_id == '18'
		}
	}, []);

	const CFWSitesFilterByPhaseFn = useCallback(phase => site => {
		if (phase === 'PHASE_GYS6') {
			return site.entity_id == 'GYS6' && site.client_id == '299'
		} else if (phase === 'PHASE_S4') {
			return site.entity_id == 'S4' && site.client_id == '299'
		}
	}, []);

	useEffect(() => {
		if (user != null) {
			validateApprovalLevel(user.roles);
			initCountry(user);
			/* validateMakroUser(user);
			validateCFWUser(user); */
			validateGYUser(user);
		}

		return () => {
			setApprovalPermissions(false);
			setSoloSite(false);
			/* setIsMakroUser(false);
			setIsCFWUser(false); */
			setIsGYUser(false);
		};
	}, []);

	useEffect(() => {
		if (userInfo != null) {
			validateMakroUser(userInfo);
			validateCFWUser(userInfo);
		}

	}, [userInfo]);

	useEffect(() => {
		if (!ready) fetchData(selectedMonth, user, userInfo, country);
	}, [selectedMonth, userInfo, country, ready]);

	return (
		<>
			<InvoiceTable
				ready={ready}
				setReady={setReady}
				country={country}
				setCountry={setCountry}
				freeSites={freeSites}
				approvalPermissions={approvalPermissions}
				selectedMonth={selectedMonth}
				setSelectedMonth={setSelectedMonth}
				invoiceSites={invoiceSites}
				// below will be passed to SummaryInvoice for bulk actions
				makroSitesFilterByPhaseFn={makroSitesFilterByPhaseFn}
				CFWSitesFilterByPhaseFn={CFWSitesFilterByPhaseFn}
				summaryInvoiceSites={summaryInvoiceSites}
				viewLevel={props.viewLevel}
				userInfo={userInfo}
			/>

			{country == "TH" && (isGYUser || (isMakroUser && !isSoloSite) || (isCFWUser && !isSoloSite)) && (
				<>
					{ready ? (
						<SummaryInvoice
							summaryInvoiceSites={summaryInvoiceSites}
							makroSitesFilterByPhaseFn={makroSitesFilterByPhaseFn}
							CFWSitesFilterByPhaseFn={CFWSitesFilterByPhaseFn}
							setSelectedMonth={setSelectedMonth}
							selectedMonth={selectedMonth}
							ready={ready}
							setReady={setReady}
							viewLevel={props.viewLevel}
							whichUser={isGYUser ? "GY" : isMakroUser ? "Makro" : isCFWUser ? "CFW" : ""}
						/>
					) : (
						<LoaderGraph style={{ height: "50vh" }} />
					)}
				</>
			)}
			<Noti />
		</>
	);
}

export default InvoiceContent;